import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, Typography, TextField, FormControlLabel, Switch, Checkbox, InputAdornment, IconButton, Tooltip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Star } from '@material-ui/icons';
import { Formik, Form } from 'formik';
import ModalDrawerSwitcher from 'components/ModalDrawerSwitcher';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import { clearServicesErrors } from 'pages/Services/store/actions';
import { loadServicesCategories, upsertService } from 'pages/Services/store/thunk';

import { convertFormattedMoneyToNumber, hoursAndMinutesToMinutes, minutesToHoursAndMinutes } from 'helpers/converters';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { validations } from './validations';

import { useStyles } from './styles';

const preparationToleranceOptions = [
  { label: '--', value: 0 },
  { label: '1 minuto', value: 1 },
  { label: '3 minutos', value: 3 },
  { label: '5 minutos', value: 5 },
  { label: '10 minutos', value: 10 },
  { label: '30 minutos', value: 30 }
];

export default function ServicesRegistration({ open, onClose, servicesPage, servicesPageSize, qp }) {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId, establishments, establishmentTypes } = useSelector(state => state.businessInfo);
  const { submit, servicesCategories, serviceId, isLoading, errors } = useSelector(state => state.services);
  const { option } = useSelector(state => state.modalDrawerSwitcher);

  const isParkingOnly = establishmentTypes.length == 1 && +establishmentTypes[0] == 1;

  const isPremiumPackage = +establishments
    ?.find(establishment => +establishment?.establishmentId == +establishmentId)
    ?.premiumPackage == 1;

  const isModal = option == 'modal';

  const [isCategoriesAndDescriptionsActive, setIsCategoriesAndDescriptionsActive] = useState(false);
  const [isSchedulingActive, setIsSchedulingActive] = useState(false);

  useEffect(() => {
    if(userId) {
      dispatch(clearServicesErrors());
      dispatch(loadServicesCategories(userId));
    }
  }, [userId]);

  useEffect(() => {
    if(submit?.categories?.filter(category => !!category)?.length > 0 || !!submit?.detailing) {
      setIsCategoriesAndDescriptionsActive(true);
    }
  }, [submit]);

  useEffect(() => {
    if(!isPremiumPackage) {
      return setIsSchedulingActive(false);
    }

    if(+hoursAndMinutesToMinutes(submit?.eventScheduling?.executionTime) == 0) {
      return setIsSchedulingActive(false);
    }

    if(+hoursAndMinutesToMinutes(submit?.eventScheduling?.executionTime) > 0) {
      return setIsSchedulingActive(true);
    }
  }, [isPremiumPackage, submit]);

  const handleSubmit = (values, { resetForm }) => {
    let params = {
      description: values?.description,
      amount: !values?.amount ? '' : convertFormattedMoneyToNumber(values?.amount),
      categories: values?.categories?.filter(category => !!category),
      detailing: values?.detailing,
      status: values?.status
    }

    if(isSchedulingActive) {
      params = {
        ...params,
        eventScheduling: {
          // ...values?.eventScheduling,
          executionTime: hoursAndMinutesToMinutes(values?.eventScheduling?.executionTime)
        }
      }
    }

    const extraProps = {
      servicesPage,
      servicesPageSize,
      qp
    }

    dispatch(upsertService(userId, establishmentId, serviceId, params, extraProps)).then(() => {
      if(isSm) {
        if(serviceId) {
          return onClose();
        }

        return resetForm();
      }

      return onClose();
    });
  }

  return(
    <ModalDrawerSwitcher
      id="services-registration-modal"
      title={serviceId ? "Editar serviço" : "Novo serviço"}
      maxWidth="sm"
      open={open}
      onClose={onClose}
    >
      <Formik
        enableReinitialize
        initialValues={submit}
        onSubmit={handleSubmit}
        validationSchema={validations}
        validateOnChange={false}
      >
        {({ ...formikProps }) => (
          <Loader isLoading={isLoading}>
            <Form style={{ height: '100%', display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
              <div>
                <Grid container>
                  <Grid item xs={12}>
                    <Input
                      name="description"
                      type="text"
                      label="Nome do serviço"
                      value={formikProps.values?.description || ""}
                      error={formikProps.errors?.description || !!errors?.description}
                      helperText={formikProps.errors?.description || errors?.description}
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Input
                      name="amount"
                      type="money"
                      label="Preço"
                      placeholder="R$ 0,00"
                      value={formikProps.values?.amount || ""}
                      error={formikProps.errors?.amount || !!errors?.amount}
                      helperText={formikProps.errors?.amount || errors?.amount}
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          classes={{
                            switchBase: classes.switchBase
                          }}
                          name="services-categories-and-descriptions"
                          type="checkbox"
                          checked={isCategoriesAndDescriptionsActive}
                          onChange={() => setIsCategoriesAndDescriptionsActive(!isCategoriesAndDescriptionsActive)}
                        />
                      }
                      label={
                        <Typography style={{ color: '#1E5168' }}>
                          <p style={{ fontSize: 17 }}>Categorias e descrições</p>
                        </Typography>
                      }
                    />
                  </Grid>
                  {isCategoriesAndDescriptionsActive && (
                    <>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          limitTags={3}
                          id="serviceCategories"
                          classes={classes}
                          options={servicesCategories}
                          value={servicesCategories?.filter(category => formikProps.values.categories?.includes(category.value)) || []}
                          onChange={(_, categories) => {
                            return formikProps.setFieldValue('categories', categories?.map(category => category?.value));
                          }}
                          getOptionSelected={(option, value) => option.value == value.value}
                          getOptionLabel={option => option.label}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              color="primary"
                              label="Categoria do serviço"
                            />
                          )}
                          renderOption={(option, { selected }) => (
                            <li color="primary">
                              <Checkbox checked={selected} />
                              {option.label}
                            </li>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Input
                          name="detailing"
                          label="Descrição do serviço"
                          value={formikProps.values.detailing || ""}
                          error={formikProps.errors.detailing || !!errors?.detailing}
                          helperText={formikProps.errors.detailing || errors?.detailing}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid container spacing={2}>
                  {!isParkingOnly && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            classes={{
                              switchBase: classes.switchBase
                            }}
                            disabled={!isPremiumPackage}
                            name="services-scheduling"
                            type="checkbox"
                            checked={isSchedulingActive}
                            onChange={() => setIsSchedulingActive(!isSchedulingActive)}
                          />
                        }
                        label={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Typography style={{ color: '#1E5168' }}>
                              <p style={{ fontSize: 17 }}>Agendamento</p>
                            </Typography>
                            {!isPremiumPackage && (
                              <Typography style={{ color: '#F5B941', lineHeight: 0.5 }}>
                                <Star />
                              </Typography>
                            )}
                          </div>
                        }
                      />
                    </Grid>
                  )}
                  {!isParkingOnly && (
                    <>
                      {isSchedulingActive && (
                        <>
                          <Grid item xs={isModal ? 6 : 12}>
                            <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA' }}>
                              <b>Tempo de serviços</b>
                              <Tooltip
                                placement='top'
                                title={
                                  <Typography>Tempo estimado de execução do serviço</Typography>
                                }
                              >
                                <IconButton>
                                  <FontAwesomeIcon
                                    color='#212121'
                                    size='xs'
                                    icon={faInfoCircle}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <Input
                              name="eventScheduling.executionTime"
                              type="timeRangePadStart"
                              placeholder="00:00"
                              value={formikProps.values?.eventScheduling?.executionTime || ""}
                              onChange={event => {
                                if(event.target.value?.length > 5) {
                                  return;
                                }

                                if(hoursAndMinutesToMinutes(event.target.value) > 1439) {
                                  return;
                                }

                                formikProps.setFieldValue("eventScheduling.executionTime", event.target.value);
                              }}
                              endAdornment={
                                <InputAdornment position='end'>
                                  <FontAwesomeIcon
                                    color='#1E5168'
                                    size='sx'
                                    icon={faClock}
                                  />
                                </InputAdornment>
                              }
                            />
                          </Grid>
                          {/* <Grid item xs={isModal ? 6 : 12}>
                            <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA' }}>
                              <b>Serviços simultâneos</b>
                              <Tooltip
                                placement='top'
                                title={
                                  <Typography>Quantidade de vezes que este serviço pode ser executado ao mesmo tempo</Typography>
                                }
                              >
                                <IconButton>
                                  <FontAwesomeIcon
                                    color='#212121'
                                    size='xs'
                                    icon={faInfoCircle}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <Input
                              name="eventScheduling.simultaneous"
                              type="number"
                              maxLength={2}
                              label="Digite aqui"
                              error={errors?.eventScheduling?.simultaneous}
                              helperText={errors?.eventScheduling?.simultaneous}
                              value={formikProps.values?.eventScheduling?.simultaneous || ""}
                              onChange={formikProps.handleChange}
                            />
                          </Grid>
                          <Grid item xs={isModal ? 6 : 12}>
                            <Typography color='primary' style={{ borderBottom: '1px solid #E4E7EA' }}>
                              <b>Intervalo de preparo</b>
                              <Tooltip
                                placement='top'
                                title={
                                  <Typography>Intervalo de tempo para preparo antes de iniciar o serviço</Typography>
                                }
                              >
                                <IconButton>
                                  <FontAwesomeIcon
                                    color='#212121'
                                    size='xs'
                                    icon={faInfoCircle}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <Select
                              name="eventScheduling.preparationTolerance"
                              label="Selecione"
                              options={preparationToleranceOptions}
                              value={formikProps.values?.eventScheduling?.preparationTolerance || ""}
                              onChange={formikProps.handleChange}
                            />
                          </Grid> */}
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button
                  type='button'
                  color='tertiary'
                  variant='outlined'
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button type='submit' color='success'>
                  Salvar
                </Button>
              </div>
            </Form>
          </Loader>
        )}
      </Formik>
    </ModalDrawerSwitcher>
  );
}